import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import logo from "../assets/Whitelogo09122025.png";
import logoMotto from "../assets/Logo_Motto.png";

interface BannerProps {
  adSpaceBanner: any[];
}

const BannerSection: React.FC<BannerProps> = ({ adSpaceBanner }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = useNavigate();

  // Banner Rotation Effect
  useEffect(() => {
    if (adSpaceBanner.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % adSpaceBanner.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [adSpaceBanner]);

  // Scroll Detection Effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navLinks = [
    { label: "BUY VEHICLE", href: "/vehicle-listing" },
    { label: "SELL VEHICLE", href: "/sell-vehicle" },
    { label: "ABOUT", href: "/about" },
    { label: "ARTICLES", href: "/articles" },
    { label: "CONTACT US", href: "/contact-us" },
  ];
  const headerTextColor = isScrolled ? "#000" : "#fff";

  // ✅ DRAWER CONTENT
  const drawerContent = (
    <Box sx={{ textAlign: "center", p: 2 }}>
      <Box display="flex" justifyContent="flex-end">
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        component="img"
        src={logoMotto}
        alt="WorkHorse Logo"
        onClick={() => {
          navigate("/");
          setMobileOpen(false);
        }}
        sx={{ width: 140, my: 2, cursor: "pointer" }}
      />

      <Divider />

      <List>
        {navLinks.map((link) => (
          <ListItem key={link.label} disablePadding>
            <ListItemButton
              component="a"
              href={link.href}
              onClick={() => setMobileOpen(false)}
              sx={{ textAlign: "center" }}
            >
              <ListItemText primary={link.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box mt={2}>
        <Button
          variant="contained"
          href="https://portal.workhorse.com.sg"
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
          sx={{
            backgroundColor: "#ffaa00",
            color: "#000",
            fontWeight: "bold",
            "&:hover": { backgroundColor: "#ffbc00" },
          }}
        >
          Login
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        // 🚀 UPDATED: Removed fixed heights and minHeights.
        // "auto" allows the container to shrink perfectly to the image height
        // preventing black bars below the image.
        height: "auto",
        minHeight: "unset", 
        overflow: "hidden",
        backgroundColor: "#000",
      }}
    >
      {/* ---------------- HEADER NAVIGATION ---------------- */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          px: { xs: 2, md: 4 },
          py: 2,
          zIndex: 100,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",

          backgroundColor: isScrolled ? "#fff" : "transparent",
          background: isScrolled
            ? "#fff"
            : "linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))",
          backdropFilter: "none",
          boxShadow: isScrolled ? "0px 4px 12px rgba(0,0,0,0.1)" : "none",
          transition: "all 0.3s ease-in-out",
        }}
      >
        {/* LOGO */}
        <Box display="flex" alignItems="center">
          <Box
            component="img"
            src={isScrolled ? logoMotto : logo}
            alt="WorkHorse Logo"
            onClick={() => navigate("/")}
            sx={{
              width: { xs: 120, sm: 140, md: 170, lg: 190 },
              height: "auto",
              cursor: "pointer",
              objectFit: "contain",
              transition: "opacity 0.3s ease-in-out",
            }}
          />
        </Box>

        {/* DESKTOP NAV */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 3,
          }}
        >
          {navLinks.map((link) => (
            <Button
              key={link.label}
              variant="text"
              href={link.href}
              sx={{
                color: headerTextColor,
                textTransform: "uppercase",
                fontSize: 14,
                fontWeight: 600,
                transition: "color 0.3s ease-in-out",
                "&:hover": {
                  backgroundColor: isScrolled
                    ? "rgba(0,0,0,0.05)"
                    : "rgba(255, 188, 0, 0.1)",
                  color: "#ffbc00",
                },
              }}
            >
              {link.label}
            </Button>
          ))}
          <Button
            variant="contained"
            href="https://portalstaging.workhorse.com.sg"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: "#ffaa00",
              color: "#000",
              fontWeight: "bold",
              ml: 1,
              "&:hover": { backgroundColor: "#ffbc00" },
            }}
          >
            Login
          </Button>
        </Box>

        {/* MOBILE MENU ICON */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            display: { md: "none" },
            color: headerTextColor,
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* MOBILE DRAWER COMPONENT */}
      <Drawer
        anchor="right"
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* ---------------- BANNER IMAGE ---------------- */}
      {adSpaceBanner.length > 0 ? (
        <Box
          component="a"
          href={adSpaceBanner[currentIndex].adSpaceThumbnailUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            // 🚀 UPDATED: Always relative so the parent container grows with the content
            position: "relative",
            inset: 0,
            display: "block",
            width: "100%",
            textDecoration: "none",
          }}
        >
          <Box
            component="img"
            src={adSpaceBanner[currentIndex].adSpaceImageUrl}
            alt="Banner"
            sx={{
              width: "100%",
              // 🚀 UPDATED: Height is auto for ALL screens.
              // This ensures the image is never stretched or placed in a box too tall for it.
              height: "auto",
              // Optional: Prevents the banner from becoming ridiculously tall on 4k screens
              // The image will just stop growing and the background will fill (if any).
              maxHeight: { lg: "80vh" }, 
              objectFit: "contain",
              objectPosition: "center",
              backgroundColor: "#000",
              display: "block", // Removes bottom inline gap
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "30%",
              background:
                "linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0))",
              zIndex: 5,
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            // Fallback stays absolute-like or just a flex container
            position: "relative",
            width: "100%",
            background:
              "linear-gradient(to right, #1e293b, rgba(0, 19, 144, 0.85))",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            // Only force height when there is NO image to prop it open
            height: { xs: "300px", md: "400px", lg: "600px" },
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            Find the Right Car for You
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BannerSection;